import { toDate } from "../../../components/utils/common";
import { DayOfWeek } from "office-ui-fabric-react";
import { MaybeDate, SortDirection } from "../../../entities/common";
import { CalendarDataSet, WorkingTimeRange, splitToDays } from "../../../store/calendar/common";
declare global {
    interface Document {
        documentMode?: any;
    }
    interface Date {
        addDays(days: number): Date;
        addWorkingDays(days: number, ...calendarSettings: CalendarDataSet[]): Date;
        isWorkingDay(...calendarSettings: CalendarDataSet[]): boolean;
        getWorkingHours(calendarSettings: CalendarDataSet): number;
        trimHours(): Date;
        clone(): Date;
        getBeginOfDay(): Date;
        getEndOfDay(): Date;
        getWeekStartDate(firstDayOfWeek: DayOfWeek): Date;
        getWeekAgo(): Date;
        getThisWeek(firstDayOfWeek: DayOfWeek): { start: Date, end: Date };
        getNextWeek(firstDayOfWeek: DayOfWeek): { start: Date, end: Date };
        getThisMonth(): { start: Date, end: Date };
        getNextMonth(): { start: Date, end: Date };
        getMonthStartDate(): Date;
        getMonthEndDate(): Date;
        getWeekAgo(): Date;
        toDateOnlyString(): string;
        toUniversalIsoDateTimeString(): string;
        setTimeOfDay(minutesSinceMidnight: number): Date;
        setDayStart(calendar: CalendarDataSet): Date;
        setDayEnd(calendar: CalendarDataSet): Date;
    }
    interface String {
        htmlEncode(): string;
        toDateFromUTC(): Date | null;
        getRenderWidth(font: string): number;
        format(...args: string[]): string;
    }
}

const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24;
export const DAYS_IN_WEEK = 7;
export const WEEK_DAYs = Array.from({ length: DAYS_IN_WEEK }, (_, day) => day);
export const DAYS_IN_MONTH_APPROX = 30;
export const MS_PER_DAY = 24 * 60 * 60 * 1000;

String.prototype.toDateFromUTC = function () {
    const pattern = new RegExp("(\\d{4})-(\\d{2})-(\\d{2})T(\\d{2}):(\\d{2}):(\\d{2})");
    const parts = this.match(pattern);
    if (!parts) {
        return null;
    }

    return new Date(Date.UTC(
        parseInt(parts[1]), parseInt(parts[2], 10) - 1, parseInt(parts[3], 10), parseInt(parts[4], 10), parseInt(parts[5], 10), parseInt(parts[6], 10), 0
    ));
};

let canvas: HTMLCanvasElement;
String.prototype.getRenderWidth = function (font: string) {
    if (!canvas) {
        canvas = document.createElement("canvas");
    }
    const context = canvas.getContext("2d");
    context!.font = font;
    const metrics = context!.measureText(`${this}`);
    return metrics.width;
};

String.prototype.format = function (...args: string[]) {
    return this.replace(/{([0-9]+)}/g, function (match, index) {
        return typeof args[index] == 'undefined' ? match : args[index];
    });
};

Date.prototype.toDateOnlyString = function (): string {
    return formatDate("yy-mm-dd", this);
};

Date.prototype.toUniversalIsoDateTimeString = function (): string {
    return toUniversalIsoDateTimeString(this);
};

Date.prototype.addDays = function (days: number): Date {
    this.setDate(this.getDate() + days);
    const dayPart = days % 1;
    if (dayPart > 0) {
        this.setTime(this.getTime() + MILLISECONDS_PER_DAY * dayPart);
    }
    return this;
};

//similar method is in DateTimeExtensions on the backend
Date.prototype.isWorkingDay = function (...calendar: CalendarDataSet[]): boolean {
    return calendar.some(_ => this.getWorkingHours(_) > 0);
};

//similar method is in DateTimeExtensions on the backend
Date.prototype.getWorkingHours = function (calendar: CalendarDataSet): number {
    const hours = calendar.exceptionsHoursMap[this.getBeginOfDay().getTime()] ?? calendar.workDayExpectedHrs[this.getDay()];
    // US 15956 (ID 11): applied HERE so one rule covers both halves - partial capacity scales the hours and the day
    // stays a working day, zero capacity leaves none and the day drops out of every walker (isWorkingDay is
    // "hours > 0"). Mirrors server DateTimeExtensions.GetWorkingHours.
    return calendar.capacityFactor ? hours * calendar.capacityFactor(this) : hours;
};

export const hasWorkingTime = (calendar: CalendarDataSet): boolean =>
    Object.values(calendar.workDayExpectedHrs ?? {}).some(_ => _ > 0)
    || Object.values(calendar.exceptionsHoursMap ?? {}).some(_ => _ > 0);

export const canWalkWorkingDays = (calendars: CalendarDataSet[]): boolean => calendars.some(hasWorkingTime);

// Task 17194 – the single bound every working-day loop in this file runs under, in CALENDAR days. Same name and
// value as the server's DateTimeExtensions.MaxDurationWalkDays, so a client preview and a server save agree.
//
// It is the length of the longest task the product allows: MaxDuration is 10 000 WORKING days, which on a five-day
// week is 14 000 calendar days. (Not imported from utils/common, where MaxDuration lives, because that module pulls
// in stores and section controls which this low-level one must not depend on.)
//
// It serves two kinds of loop. A duration WALK consumes the task span itself, so the bound only has to be large
// enough for the longest valid Duration to walk end to end. A SEARCH – "where is the next/previous day this calendar
// works?" – is the one that can run away: a 0-hour work week, a resource at 0% Max Units / 100% Non-project Work, or
// a long calendar exception leaves EVERY day non-working (Bug 17179 / Bug 17185). Exhausting a search is a real
// answer, not an error – "no working day in the searched window" – and every caller handles it (nextWorkingDayStart
// returns undefined, addWorkingDays hands back the day it was given, hasAnyWorkingDay returns false).
export const MAX_DURATION_WALK_DAYS = 14000;

// US 15956 (ID 11) / Bug 17192 – can these calendars carry any work at or after `from`? Exactly the pair every
// duration walk below already guards itself with, named for the callers that need to ask it BEFORE walking: a
// 0-hour work week has no working day by its week alone (canWalkWorkingDays), while a capacity factor can leave an
// otherwise normal week with none (hasAnyWorkingDay). Mirrors the question the server's
// TaskWorkloadHandler.DistributionStartFor answers by walking.
export const hasWorkingDayFrom = (from: Date, calendars: CalendarDataSet[]): boolean =>
    canWalkWorkingDays(calendars) && hasAnyWorkingDay(from, 1, calendars);

// US 15956 (ID 11) / Bug 17179: a capacityFactor can leave every day of an otherwise normal work week with no
// hours, which hasWorkingTime cannot see. The walks would then spend their whole guard calling nextWorkingDayStart,
// which re-scans the same window each time. Free for any calendar without a capacity factor. Mirrors server
// DateTimeExtensions.HasAnyWorkingDay.
function hasAnyWorkingDay(from: Date, step: number, calendars: CalendarDataSet[]): boolean {
    if (!calendars.some(_ => _.capacityFactor)) {
        return true;
    }
    for (let i = 0; i < MAX_DURATION_WALK_DAYS; i++) {
        const day = from.clone().addDays(step * i);
        if (calendars.some(c => day.isWorkingDay(c))) {
            return true;
        }
    }
    return false;
}


Date.prototype.addWorkingDays = function (days: number, ...calendars: CalendarDataSet[]): Date {
    if (days === 0 || calendars.length === 0 || !canWalkWorkingDays(calendars)) {
        return this.clone();
    }

    const date = this.clone();
    const origin = date.clone();
    days = days > 0 ? Math.ceil(days) : Math.floor(days);

    const calendarDaysPerWeek: number = 7;
    const workDaysPerWeek = WEEK_DAYs.filter(dow => calendars.some(c => (c.workDayExpectedHrs[dow] ?? 0) > 0)).length;

    let step = days > 0 ? 1 : -1;

    if (workDaysPerWeek > 0) {
        const wholeWeeks = Math.floor(Math.abs(days) / workDaysPerWeek);
        date.addDays(step * wholeWeeks * calendarDaysPerWeek);

        // Snap back onto a working day, correcting the whole-week jump above.
        //
        // Bug 17184 - bounded, because a work week alone does not prove a day is workable: a calendar exception, or
        // a resource with no capacity before some date (US 15956 ID 11), can leave EVERY earlier day non-working and
        // this would scan into the infinite past. Exhausting the scan means there is no working day at or before the
        // estimate, so the correction simply does not apply and the forward walk below starts from the estimate --
        // which is the right answer, and the wrong one is giving up and returning the input: a resource at 0% Max
        // Units until 1 Sep asked for its next working day from 17 Aug got 17 Aug back, so the task panel never
        // moved the Start off the day that resource cannot work and previewed "Date is not available" on it.
        // Mirrors server DateTimeExtensions.AddWorkingDays.
        const snapped = date.clone();
        for (let guard = 0; !snapped.isWorkingDay(...calendars) && guard < MAX_DURATION_WALK_DAYS; guard++) {
            snapped.addDays(-step);
        }
        if (snapped.isWorkingDay(...calendars)) {
            date.setTime(snapped.getTime());
        }

        const from = step > 0 ? origin : date;
        const to = step > 0 ? date : origin;
        days -= step * (getWorkingDaysBetweenDates(from, to, ...calendars) - (origin.isWorkingDay(...calendars) ? 1 : 0));
    }

    step = days > 0 ? 1 : -1;
    let scan = MAX_DURATION_WALK_DAYS;
    while (days !== 0) {
        if (scan-- <= 0) {
            return this.clone();
        }
        date.addDays(step);
        if (date.isWorkingDay(...calendars)) {
            days -= step;
        }
    }

    return date;
}

//similar method is in DateTimeExtensions on the backend
export function getWorkingDaysBetweenDates(startDate: Date, endDate: Date, ...calendar: CalendarDataSet[]): number {
    startDate = startDate.getBeginOfDay();
    endDate = endDate.getBeginOfDay();

    if (endDate < startDate) {
        return 0;
    }

    let tmp = startDate.clone();
    let counter = 0;

    do
    {
        if (calendar.some(_ => tmp.isWorkingDay(_)))
        {
            counter++;
        }
        tmp = tmp.addDays(1);
    }
    while (tmp <= endDate);

    return counter;
}

export function getDaysBetweenDates(start: Date, end: Date): Date[] {
    const result: Date[] = [];
    start = toDate(start)?.clone()!;

    while (start <= end) {
        result.push(start.getBeginOfDay());
        start.addDays(1);
    }

    return result;
}

export function getDuration(start: Date, end: Date): number {
    return Math.round(diffDays(start.getBeginOfDay(), end.getEndOfDay()));
}

export function getWorkingHoursBetweenDates(startDate: Date, endDate: Date, calendar: CalendarDataSet): number {
    if (endDate < startDate) {
        return 0;
    }

    const calendarDaysPerWeek = 7;

    let calendarTotalDays = Math.round(diffDays(startDate.getBeginOfDay(), endDate.getEndOfDay()));
    let wholeWeeks = Math.floor(calendarTotalDays / calendarDaysPerWeek);

    startDate = startDate.getBeginOfDay();
    endDate = endDate.getBeginOfDay();

    let exceptionWorkingHours = splitToDays(calendar.exceptions
        .filter(_ => _.start <= endDate && _.end >= startDate))
        .filter(_ => startDate <= _.date && _.date <= endDate && calendar.workDayExpectedHrs[_.date.getDay()] != _.expectedHrs)
        .reduce((sum, _) => (sum + _.expectedHrs - calendar.workDayExpectedHrs[_.date.getDay()]), 0);

    let extraWorkingHours = calendarTotalDays % calendarDaysPerWeek == 0 ? 0 :
        Array.from(Array(7).keys())
            .filter(_ => startDate.getDay() <= endDate.getDay()
                ? (_ >= startDate.getDay() && _ <= endDate.getDay())
                : (_ >= startDate.getDay() || _ <= endDate.getDay()))
            .reduce((sum, _) => (sum + calendar.workDayExpectedHrs[_]), 0);

    return wholeWeeks * calendar.workingHoursPerWeek + extraWorkingHours + exceptionWorkingHours;
}

const DEFAULT_DAY_START_MINUTES = 480;  // 8:00 AM
const DEFAULT_DAY_END_MINUTES = 1020;   // 5:00 PM

/**
 * Returns the day start time (first working time From) in minutes since midnight for the given date.
 * Similar to DateTimeExtensions.GetDayStartMinutes on the backend.
 */
export function getDayStartMinutes(date: Date, calendar: CalendarDataSet): number {
    const beginOfDay = date.getBeginOfDay();
    const exception = calendar.exceptions.find(_ =>
        _.start <= beginOfDay && beginOfDay <= _.end && _.days.includes(beginOfDay.getDay()));
    const ranges = exception?.workingTimes ?? calendar.workDayWorkingTimes?.[beginOfDay.getDay()];
    if (!ranges || ranges.length === 0) {
        return DEFAULT_DAY_START_MINUTES;
    }
    return Math.min(...ranges.map(_ => _.from));
}

/**
 * Returns the day end time (last working time To) in minutes since midnight for the given date.
 * Similar to DateTimeExtensions.GetDayEndMinutes on the backend.
 */
export function getDayEndMinutes(date: Date, calendar: CalendarDataSet): number {
    const beginOfDay = date.getBeginOfDay();
    const exception = calendar.exceptions.find(_ =>
        _.start <= beginOfDay && beginOfDay <= _.end && _.days.includes(beginOfDay.getDay()));
    const ranges = exception?.workingTimes ?? calendar.workDayWorkingTimes?.[beginOfDay.getDay()];
    if (!ranges || ranges.length === 0) {
        return DEFAULT_DAY_END_MINUTES;
    }
    return Math.max(...ranges.map(_ => _.to));
}

// --- Decimal duration walking (US 16555) ----------------------------------------------------------
// Mirror of PMOExpress.DAL.Extensions.DateTimeExtensions. Duration is a decimal number of working
// days; Start/Due times are derived with the "proportional day-fraction walk": each fully-traversed
// working day contributes 1.0, a partial day contributes (working-minutes traversed that day) /
// (that day's total working-minutes). Working days are decided by the assignee calendars; intra-day
// working-time ranges come from the global calendar (matching day-start/day-end snapping), falling back
// to the assignee calendars on a day the global calendar does not work — see getDayWorkingTimeRanges.

const DURATION_EPSILON = 0.0000001;

export function minutesOfDay(date: Date): number {
    return date.getHours() * 60 + date.getMinutes();
}

export function getWorkingTimeRanges(date: Date, calendar: CalendarDataSet): WorkingTimeRange[] {
    const beginOfDay = date.getBeginOfDay();
    const exception = calendar.exceptions.find(_ =>
        _.start <= beginOfDay && beginOfDay <= _.end && _.days.includes(beginOfDay.getDay()));
    const ranges = exception?.workingTimes ?? calendar.workDayWorkingTimes?.[beginOfDay.getDay()] ?? [];
    return [...ranges].sort((a, b) => a.from - b.from);
}

// Bug 17148 – the intra-day working-time ranges the walks below measure a day with. The convention is the GLOBAL
// calendar's ranges (what day-start/day-end snapping uses), which holds as long as every assignee work week is a
// SUBSET of the project's. It breaks for a DISJOINT one — a resource who works only weekends — where the global
// calendar has no ranges at all for a day the assignee DOES work: that day measures 0 working minutes, consumes
// none of the duration, and the walks never finish.
//
// So fall back to the working times of the calendars that DO consider the day working, and finally to the default
// working day, making every day these walks treat as working have working time in it. Mirrors the server
// GetDayWorkingTimeRanges.
export function getDayWorkingTimeRanges(date: Date, calendars: CalendarDataSet[], globalCalendar: CalendarDataSet): WorkingTimeRange[] {
    const globalRanges = getWorkingTimeRanges(date, globalCalendar);
    if (globalRanges.length > 0) {
        return globalRanges;
    }
    const workingCalendars = calendars.filter(_ => date.isWorkingDay(_));
    if (workingCalendars.length === 0) {
        return globalRanges;
    }
    const merged = mergeWorkingTimeRanges(workingCalendars.flatMap(_ => getWorkingTimeRanges(date, _)));
    // A calendar can mark the day working through workDayExpectedHrs/exceptions without carrying working
    // times for it; use the same default the day-start/day-end helpers fall back to.
    return merged.length > 0 ? merged : [{ from: DEFAULT_DAY_START_MINUTES, to: DEFAULT_DAY_END_MINUTES }];
}

// Union of the ranges: ordered by start and coalesced, so overlapping assignee work shifts are counted once.
function mergeWorkingTimeRanges(ranges: WorkingTimeRange[]): WorkingTimeRange[] {
    const sorted = ranges.filter(_ => _.to > _.from).sort((a, b) => a.from - b.from);
    const merged: WorkingTimeRange[] = [];
    for (const range of sorted) {
        const last = merged[merged.length - 1];
        if (last && range.from <= last.to) {
            last.to = Math.max(last.to, range.to);
        } else {
            merged.push({ ...range });
        }
    }
    return merged;
}

// Day start/end for a day measured with getDayWorkingTimeRanges — identical to getDayStartMinutes/
// getDayEndMinutes whenever the global calendar has working times for the day (the ranges are then the
// global ones), and only differs on the disjoint-work-week day the global calendar knows nothing about.
export function dayStartMinutesFor(date: Date, calendars: CalendarDataSet[], globalCalendar: CalendarDataSet): number {
    const ranges = getDayWorkingTimeRanges(date, calendars, globalCalendar);
    return ranges.length > 0 ? ranges[0].from : getDayStartMinutes(date, globalCalendar);
}

export function dayEndMinutesFor(date: Date, calendars: CalendarDataSet[], globalCalendar: CalendarDataSet): number {
    const ranges = getDayWorkingTimeRanges(date, calendars, globalCalendar);
    return ranges.length > 0 ? ranges[ranges.length - 1].to : getDayEndMinutes(date, globalCalendar);
}

// Task 17266 – the instant ONE assignee's own span geometry opens at: the span's Start, moved back onto the start of
// THEIR working day whenever the Start is the start of the span's day.
//
// A span's day boundaries are the PROJECT's (getDayWorkingTimeRanges reads the global calendar's working times
// whenever it has any), but the per-assignee walks measure a day with the ASSIGNEE's own ranges. For a resource whose
// shift starts EARLIER than the project day — 07:00–15:00 against 08:00–17:00 — the two conventions disagree about
// the very first instant: the task's 08:00 Start is the beginning of its day, and an hour into the resource's. Read
// literally it clips their first day to 7 of its 8 hours, so a 1d / 8h task distributed 7 + 1 and the leftover hour
// stretched it to 2 days. Duration counts WHOLE working days, so a task that occupies a day whole gives each assignee
// that whole day of theirs; only a GENUINE mid-day start — a decimal lag, a to-Finish pin — is left where it is.
// Mirrors server DateTimeExtensions.ToOwnDayStart.
export function toOwnDayStart(startInstant: Date, assigneeCalendar: CalendarDataSet, globalCalendar: CalendarDataSet): Date {
    if (minutesOfDay(startInstant) !== dayStartMinutesFor(startInstant, [assigneeCalendar], globalCalendar)) {
        return startInstant;
    }
    const ownRanges = getWorkingTimeRanges(startInstant, assigneeCalendar);
    return ownRanges.length > 0 ? startInstant.clone().setTimeOfDay(ownRanges[0].from) : startInstant;
}

// toOwnDayStart for a finish-anchored span: a Due at the end of the span's day is the end of the ASSIGNEE's day too,
// so a shift running past the project's day end — 09:00–18:00 against 08:00–17:00 — is not read as finishing early.
// Mirrors server DateTimeExtensions.ToOwnDayEnd.
export function toOwnDayEnd(dueInstant: Date, assigneeCalendar: CalendarDataSet, globalCalendar: CalendarDataSet): Date {
    if (minutesOfDay(dueInstant) !== dayEndMinutesFor(dueInstant, [assigneeCalendar], globalCalendar)) {
        return dueInstant;
    }
    const ownRanges = getWorkingTimeRanges(dueInstant, assigneeCalendar);
    return ownRanges.length > 0 ? dueInstant.clone().setTimeOfDay(ownRanges[ownRanges.length - 1].to) : dueInstant;
}

// Does this DAY hold any working time — for the project calendar OR for any of `calendars`? The union is what
// getDayWorkingTimeRanges measures the day with, so this is the "somebody works this day" test that goes with
// dayStartMinutesFor/dayEndMinutesFor. Mirrors the server's HasWorkingTime; not to be confused with the
// whole-calendar hasWorkingTime above (which asks whether a calendar has any working time at all).
export function dayHasWorkingTime(date: Date, calendars: CalendarDataSet[], globalCalendar: CalendarDataSet): boolean {
    return getDayWorkingTimeRanges(date, calendars, globalCalendar).length > 0;
}

function workingMinutesFrom(fromMinutes: number, ranges: WorkingTimeRange[]): number {
    return ranges.reduce((sum, r) => sum + Math.max(0, r.to - Math.max(fromMinutes, r.from)), 0);
}

function workingMinutesUntil(toMinutes: number, ranges: WorkingTimeRange[]): number {
    return ranges.reduce((sum, r) => sum + Math.max(0, Math.min(toMinutes, r.to) - r.from), 0);
}

function workingMinutesBetween(fromMinutes: number, toMinutes: number, ranges: WorkingTimeRange[]): number {
    return ranges.reduce((sum, r) => sum + Math.max(0, Math.min(toMinutes, r.to) - Math.max(fromMinutes, r.from)), 0);
}

function addWorkingMinutesForward(fromMinutes: number, minutes: number, ranges: WorkingTimeRange[]): number {
    for (const range of ranges) {
        const segmentStart = Math.max(fromMinutes, range.from);
        if (segmentStart >= range.to) {
            continue;
        }
        const segmentAvailable = range.to - segmentStart;
        if (minutes <= segmentAvailable) {
            return segmentStart + minutes;
        }
        minutes -= segmentAvailable;
    }
    return ranges.length > 0 ? ranges[ranges.length - 1].to : fromMinutes;
}

function subtractWorkingMinutesBackward(fromMinutes: number, minutes: number, ranges: WorkingTimeRange[]): number {
    for (let i = ranges.length - 1; i >= 0; i--) {
        const range = ranges[i];
        const segmentEnd = Math.min(fromMinutes, range.to);
        if (segmentEnd <= range.from) {
            continue;
        }
        const segmentAvailable = segmentEnd - range.from;
        if (minutes <= segmentAvailable) {
            return segmentEnd - minutes;
        }
        minutes -= segmentAvailable;
    }
    return ranges.length > 0 ? ranges[0].from : fromMinutes;
}

export function nextWorkingInstant(instant: Date, calendars: CalendarDataSet[], globalCalendar: CalendarDataSet, inclusiveRangeEnd: boolean = false): Date {
    if (!canWalkWorkingDays(calendars)) {
        return instant;
    }

    const minutes = minutesOfDay(instant);
    for (let i = 0; i < MAX_DURATION_WALK_DAYS; i++) {
        const day = instant.clone().addDays(i);
        if (!calendars.some(c => day.isWorkingDay(c))) {
            continue;
        }
        const ranges = getDayWorkingTimeRanges(day, calendars, globalCalendar);
        if (ranges.length === 0) {
            continue;
        }
        const floor = i === 0 ? minutes : ranges[0].from;
        for (const range of ranges) {
            if (floor <= range.from) {
                return day.setTimeOfDay(range.from);
            }
            // Task 13311: with inclusiveRangeEnd, an instant exactly at a range end (working-day end / before a
            // break) stays there instead of snapping forward — a zero-duration task in keep mode lands there.
            if (inclusiveRangeEnd ? floor <= range.to : floor < range.to) {
                return day.setTimeOfDay(floor);
            }
        }
    }
    return instant;
}

export function prevWorkingInstant(instant: Date, calendars: CalendarDataSet[], globalCalendar: CalendarDataSet): Date {
    if (!canWalkWorkingDays(calendars)) {
        return instant;
    }

    const minutes = minutesOfDay(instant);
    for (let i = 0; i < MAX_DURATION_WALK_DAYS; i++) {
        const day = instant.clone().addDays(-i);
        if (!calendars.some(c => day.isWorkingDay(c))) {
            continue;
        }
        const ranges = getDayWorkingTimeRanges(day, calendars, globalCalendar);
        if (ranges.length === 0) {
            continue;
        }
        const ceil = i === 0 ? minutes : ranges[ranges.length - 1].to;
        for (let k = ranges.length - 1; k >= 0; k--) {
            const range = ranges[k];
            if (ceil >= range.to) {
                return day.setTimeOfDay(range.to);
            }
            if (ceil > range.from) {
                return day.setTimeOfDay(ceil);
            }
        }
    }
    return instant;
}

// Returns an instant strictly after `date` so the duration walks below cannot spin in place, or UNDEFINED when
// there is no working day left at all.
//
// Bug 17185: undefined is the whole point, and the previous "advance one calendar day" fallback was wrong twice
// over. A resource at 0% Max Units FROM some date (US 15956 ID 11) has no working day anywhere in its future, so
// past that date every call scans its whole guard and finds nothing — and the caller, handed a day one step along,
// asked again from there, making every iteration of a walk pay for a full scan: the task panel froze. The day
// handed back is also one the assignee does NOT work, which the walks then measured on the GLOBAL calendar's
// ranges (getDayWorkingTimeRanges falls back to them), counting capacity nobody has. Telling the caller the walk
// is over ends it at the last day the assignee really works, after exactly one exhausted scan.
function nextWorkingDayStart(date: Date, calendars: CalendarDataSet[], globalCalendar: CalendarDataSet): Date | undefined {
    if (canWalkWorkingDays(calendars)) {
        for (let i = 1; i < MAX_DURATION_WALK_DAYS; i++) {
            const day = date.clone().addDays(i);
            if (calendars.some(c => day.isWorkingDay(c))) {
                return day.setTimeOfDay(dayStartMinutesFor(day, calendars, globalCalendar));
            }
        }
    }
    return undefined;
}

// Returns an instant strictly before `date` so the duration walks below cannot spin in place, or UNDEFINED when
// there is no working day left at all — the mirror of nextWorkingDayStart, for a LEADING zero segment.
function prevWorkingDayEnd(date: Date, calendars: CalendarDataSet[], globalCalendar: CalendarDataSet): Date | undefined {
    if (canWalkWorkingDays(calendars)) {
        for (let i = 1; i < MAX_DURATION_WALK_DAYS; i++) {
            const day = date.clone().addDays(-i);
            if (calendars.some(c => day.isWorkingDay(c))) {
                return day.setTimeOfDay(dayEndMinutesFor(day, calendars, globalCalendar));
            }
        }
    }
    return undefined;
}

export function addDuration(startInstant: Date, duration: number, calendars: CalendarDataSet[], globalCalendar: CalendarDataSet): Date {
    if (duration <= 0 || !canWalkWorkingDays(calendars) || !hasAnyWorkingDay(startInstant, 1, calendars)) {
        return startInstant.clone();
    }

    let cursor = nextWorkingInstant(startInstant, calendars, globalCalendar);
    let remaining = duration;

    for (let guard = 0; guard < MAX_DURATION_WALK_DAYS; guard++) {
        const ranges = getDayWorkingTimeRanges(cursor, calendars, globalCalendar);
        const dayTotalMinutes = ranges.reduce((s, r) => s + (r.to - r.from), 0);
        if (dayTotalMinutes <= 0) {
            const nextAfterEmptyDay = nextWorkingDayStart(cursor, calendars, globalCalendar);
            if (!nextAfterEmptyDay) {
                break;
            }
            cursor = nextAfterEmptyDay;
            continue;
        }
        const cursorMinutes = minutesOfDay(cursor);
        const availableFraction = workingMinutesFrom(cursorMinutes, ranges) / dayTotalMinutes;
        if (remaining <= availableFraction + DURATION_EPSILON) {
            const endMinutes = addWorkingMinutesForward(cursorMinutes, remaining * dayTotalMinutes, ranges);
            return cursor.setTimeOfDay(Math.round(endMinutes));
        }
        remaining -= availableFraction;
        // Bug 17185: out of working days with duration still to place — the span cannot reach past the last day the
        // assignees work, so it ends there.
        const next = nextWorkingDayStart(cursor, calendars, globalCalendar);
        if (!next) {
            break;
        }
        cursor = next;
    }
    return cursor.setTimeOfDay(dayEndMinutesFor(cursor, calendars, globalCalendar));
}

// Working hours a single calendar contributes over `duration` working days from the start instant. Walks the
// calendar exactly like addDuration — consuming each day's available working-time fraction (so a mid-day start
// only counts the remainder of that day) — but accumulates hours (fraction × the day's expected hours) instead
// of finding the end instant. Respects the work-week, exceptions, partial start/end days and non-uniform daily
// hours. Mirror of DateTimeExtensions.GetWorkingHoursForDuration; used by Fixed Units effort.
// Mirrors server DateTimeExtensions.GetWorkingHoursForDuration.
export function getWorkingHoursForDuration(startInstant: Date, duration: number, calendar: CalendarDataSet): number {
    if (duration <= 0 || !canWalkWorkingDays([calendar]) || !hasAnyWorkingDay(startInstant, 1, [calendar])) {
        return 0;
    }

    const calendars = [calendar];
    let cursor = nextWorkingInstant(startInstant, calendars, calendar);
    let remaining = duration;
    let total = 0;
    for (let guard = 0; guard < MAX_DURATION_WALK_DAYS; guard++) {
        const ranges = getWorkingTimeRanges(cursor, calendar);
        const dayTotalMinutes = ranges.reduce((s, r) => s + (r.to - r.from), 0);
        if (dayTotalMinutes <= 0) {
            const nextAfterEmptyDay = nextWorkingDayStart(cursor, calendars, calendar);
            if (!nextAfterEmptyDay) {
                break;
            }
            cursor = nextAfterEmptyDay;
            continue;
        }
        const cursorMinutes = minutesOfDay(cursor);
        const availableFraction = workingMinutesFrom(cursorMinutes, ranges) / dayTotalMinutes;
        const dayHours = cursor.getWorkingHours(calendar);
        if (dayHours <= 0) {
            // No capacity today: the ranges still exist, but the day is not a working one.
            // Bug 17185: this branch consumes NO duration, so with a capacity factor that is zero from some date on
            // it used to run the whole guard, each iteration paying for an exhausted scan inside nextWorkingDayStart
            // — the frozen task panel. `remaining` simply cannot be placed any more.
            const nextAfterZeroCapacityDay = nextWorkingDayStart(cursor, calendars, calendar);
            if (!nextAfterZeroCapacityDay) {
                break;
            }
            cursor = nextAfterZeroCapacityDay;
            continue;
        }
        if (remaining <= availableFraction + DURATION_EPSILON) {
            total += remaining * dayHours;
            break;
        }
        total += availableFraction * dayHours;
        remaining -= availableFraction;
        const next = nextWorkingDayStart(cursor, calendars, calendar);
        if (!next) {
            break;
        }
        cursor = next;
    }
    return total;
}

export function subtractDuration(dueInstant: Date, duration: number, calendars: CalendarDataSet[], globalCalendar: CalendarDataSet): Date {
    if (duration <= 0 || !canWalkWorkingDays(calendars) || !hasAnyWorkingDay(dueInstant, -1, calendars)) {
        return dueInstant.clone();
    }

    // A date-only due (00:00, never a real working time) denotes the end of that day.
    let due = dueInstant;
    if (minutesOfDay(dueInstant) === 0) {
        due = dueInstant.clone().setTimeOfDay(dayEndMinutesFor(dueInstant, calendars, globalCalendar));
    }

    let cursor = prevWorkingInstant(due, calendars, globalCalendar);
    let remaining = duration;

    for (let guard = 0; guard < MAX_DURATION_WALK_DAYS; guard++) {
        const ranges = getDayWorkingTimeRanges(cursor, calendars, globalCalendar);
        const dayTotalMinutes = ranges.reduce((s, r) => s + (r.to - r.from), 0);
        if (dayTotalMinutes <= 0) {
            const prevBeforeEmptyDay = prevWorkingDayEnd(cursor, calendars, globalCalendar);
            if (!prevBeforeEmptyDay) {
                break;
            }
            cursor = prevBeforeEmptyDay;
            continue;
        }
        const cursorMinutes = minutesOfDay(cursor);
        const availableFraction = workingMinutesUntil(cursorMinutes, ranges) / dayTotalMinutes;
        if (remaining <= availableFraction + DURATION_EPSILON) {
            const startMinutes = subtractWorkingMinutesBackward(cursorMinutes, remaining * dayTotalMinutes, ranges);
            return cursor.setTimeOfDay(Math.round(startMinutes));
        }
        remaining -= availableFraction;
        // Bug 17185: no working day left behind us — the span starts at the first day the assignees do work.
        const prev = prevWorkingDayEnd(cursor, calendars, globalCalendar);
        if (!prev) {
            break;
        }
        cursor = prev;
    }
    return cursor.setTimeOfDay(dayStartMinutesFor(cursor, calendars, globalCalendar));
}

export function getDurationBetween(startInstant: Date, dueInstant: Date, calendars: CalendarDataSet[], globalCalendar: CalendarDataSet): number {
    // Bug 17179: the loop below ends by REACHING dueInstant, which a cursor that cannot advance never does.
    if (dueInstant.getTime() <= startInstant.getTime() || !canWalkWorkingDays(calendars) || !hasAnyWorkingDay(startInstant, 1, calendars)) {
        return 0;
    }

    let cursor = nextWorkingInstant(startInstant, calendars, globalCalendar);
    if (cursor.getTime() > dueInstant.getTime()) {
        return 0;
    }

    let total = 0;
    for (let guard = 0; guard < MAX_DURATION_WALK_DAYS; guard++) {
        const ranges = getDayWorkingTimeRanges(cursor, calendars, globalCalendar);
        const dayTotalMinutes = ranges.reduce((s, r) => s + (r.to - r.from), 0);
        const sameDay = cursor.getBeginOfDay().getTime() === dueInstant.getBeginOfDay().getTime();
        if (dayTotalMinutes > 0) {
            const endMinutes = sameDay ? minutesOfDay(dueInstant) : ranges[ranges.length - 1].to;
            total += workingMinutesBetween(minutesOfDay(cursor), endMinutes, ranges) / dayTotalMinutes;
            if (sameDay) {
                break;
            }
        }
        if (cursor.getBeginOfDay().getTime() >= dueInstant.getBeginOfDay().getTime()) {
            break;
        }
        // Bug 17185: no working day left before the Due, so the span is worth what has been counted so far — NOT the
        // remaining calendar days, which the global-calendar fallback used to hand back as if the assignee worked
        // them (17 Aug → 15 Sep measured 22 for a resource whose capacity ends on 1 Sep, against its real 11).
        const next = nextWorkingDayStart(cursor, calendars, globalCalendar);
        if (!next || next.getTime() > dueInstant.getTime()) {
            break;
        }
        cursor = next;
    }
    return Math.round(total * 100) / 100;
}

// Returns a new Date on the same calendar day with the time set to minutesSinceMidnight.
Date.prototype.setTimeOfDay = function (minutesSinceMidnight: number): Date {
    const d = this.getBeginOfDay();
    const hours = Math.floor(minutesSinceMidnight / 60);
    const minutes = minutesSinceMidnight % 60;
    d.setHours(hours, minutes, 0, 0);
    return d;
};

Date.prototype.setDayStart = function (calendar: CalendarDataSet): Date {
    return this.setTimeOfDay(getDayStartMinutes(this, calendar));
};

Date.prototype.setDayEnd = function (calendar: CalendarDataSet): Date {
    return this.setTimeOfDay(getDayEndMinutes(this, calendar));
};

// UTC components carry the working time so FormatUniversalDateTime (timeZone:'UTC') displays it as-is.
function toUniversalIso(date: Date, minutesSinceMidnight: number): string {
    const hh = String(Math.floor(minutesSinceMidnight / 60)).padStart(2, '0');
    const mi = String(minutesSinceMidnight % 60).padStart(2, '0');
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}:00.000Z`;
}

export function toUniversalIsoDateTimeString(date: Date): string {
    return toUniversalIso(date, date.getHours() * 60 + date.getMinutes());
}

Date.prototype.clone = function (): Date {
    const copiedDate = new Date(this.getTime());
    return copiedDate;
};

Date.prototype.trimHours = function (): Date {
    const date = new Date(this);
    date.setHours(0, 0, 0, 0);
    return date;
};

Date.prototype.getBeginOfDay = function (): Date {
    return this.trimHours();
};

Date.prototype.getEndOfDay = function (): Date {
    const date = new Date(this);
    date.setHours(23, 59, 59, 999);
    return date;
}

Date.prototype.getMonthStartDate = function () {
    return new Date(this.getFullYear(), this.getMonth());
}

Date.prototype.getMonthEndDate = function () {
    return new Date(this.getFullYear(), this.getMonth() + 1, 0);
}

Date.prototype.getWeekStartDate = function (firstDayOfWeek: DayOfWeek) {
    const result = new Date(this);
    // Calculate days passed from the first day of week
    const daysPassedFromStartOfWeek = (this.getDay() - firstDayOfWeek + 7) % 7;
    // Subtract days passed to get to the start of the week
    result.setDate(this.getDate() - daysPassedFromStartOfWeek);
    return result;
}

Date.prototype.getThisWeek = function (firstDayOfWeek: DayOfWeek) {
    const start = this.getWeekStartDate(firstDayOfWeek).getBeginOfDay();
    return { start, end: start.clone().addDays(6).getEndOfDay() }
}

Date.prototype.getNextWeek = function (firstDayOfWeek: DayOfWeek) {
    return this.clone().addDays(7).getThisWeek(firstDayOfWeek);
}

Date.prototype.getThisMonth = function () {
    const start = this.getMonthStartDate().getBeginOfDay();
    const end = this.getMonthEndDate().getEndOfDay();
    return { start, end };
}

Date.prototype.getNextMonth = function () {
    return this.getMonthEndDate().addDays(1).getThisMonth();
}

Date.prototype.getWeekAgo = function () {
    const date = this.getBeginOfDay();
    date.setDate(date.getDate() - 7);
    return date;
}

const _ticksTo1970: number = (((1970 - 1) * 365 + Math.floor(1970 / 4) - Math.floor(1970 / 100) +
    Math.floor(1970 / 400)) * MILLISECONDS_PER_DAY * 10000);

export function formatDate(format: any, date: any, settings?: any) {
    if (!date) {
        return "";
    }

    let iFormat: any,
        dayNamesShort = (settings ? settings.dayNamesShort : null) ||
            ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
        dayNames = (settings ? settings.dayNames : null) ||
            ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        monthNamesShort = (settings ? settings.monthNamesShort : null) ||
            ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
        monthNames = (settings ? settings.monthNames : null) ||
            [
                "January", "February", "March", "April", "May", "June", "July", "August", "September", "October",
                "November", "December"
            ],
        // Check whether a format character is doubled
        lookAhead = (match: any) => {
            const matches = (iFormat + 1 < format.length && format.charAt(iFormat + 1) === match);
            if (matches) {
                iFormat++;
            }
            return matches;
        },
        // Format a number, with leading zero if necessary
        formatNumber = (match: any, value: any, len: any) => {
            let num = "" + value;
            if (lookAhead(match)) {
                while (num.length < len) {
                    num = "0" + num;
                }
            }
            return num;
        },
        // Format a name, short or long as requested
        formatName =
            (match: any, value: any, shortNames: any, longNames: any) => lookAhead(match)
                ? longNames[value]
                : shortNames[value],
        output = "",
        literal = false;

    if (date) {
        for (iFormat = 0; iFormat < format.length; iFormat++) {
            if (literal) {
                if (format.charAt(iFormat) === "'" && !lookAhead("'")) {
                    literal = false;
                } else {
                    output += format.charAt(iFormat);
                }
            } else {
                switch (format.charAt(iFormat)) {
                    case "d":
                        output += formatNumber("d", date.getDate(), 2);
                        break;
                    case "D":
                        output += formatName("D", date.getDay(), dayNamesShort, dayNames);
                        break;
                    case "o":
                        output += formatNumber("o",
                            Math.round((new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime() -
                                new Date(date.getFullYear(), 0, 0).getTime()) /
                                86400000),
                            3);
                        break;
                    case "m":
                        output += formatNumber("m", date.getMonth() + 1, 2);
                        break;
                    case "M":
                        output += formatName("M", date.getMonth(), monthNamesShort, monthNames);
                        break;
                    case "y":
                        output += (lookAhead("y")
                            ? date.getFullYear()
                            : (date.getYear() % 100 < 10 ? "0" : "") + date.getYear() % 100);
                        break;
                    case "@":
                        output += date.getTime();
                        break;
                    case "!":
                        output += date.getTime() * 10000 + _ticksTo1970;
                        break;
                    case "'":
                        if (lookAhead("'")) {
                            output += "'";
                        } else {
                            literal = true;
                        }
                        break;
                    default:
                        output += format.charAt(iFormat);
                }
            }
        }
    }
    return output;
}

export function min(dates: (Date | string | undefined | null)[]): Date | undefined {
    return toDate(dates.reduce((accumulator, currentValue) => {
        const d1 = toDate(accumulator);
        const d2 = toDate(currentValue);
        return (d1 && d2)
            ? (d1 < d2 ? d1 : d2)
            : (d1 || d2);
    }, null));
}

export function max(dates: MaybeDate[]): Date | undefined {
    return toDate(dates.reduce((accumulator, currentValue) => {
        const d1 = toDate(accumulator);
        const d2 = toDate(currentValue);
        return (d1 && d2)
            ? (d1 > d2 ? d1 : d2)
            : (d1 || d2);
    }, null));
}

export function maxBy<T>(array: T[], select: (props: T) => MaybeDate): T | null {
    return array.reduce((accumulator: T | null, element) => {
        if (!accumulator) {
            return element;
        }

        const d1 = toDate(accumulator && select(accumulator));
        const d2 = toDate(select(element));
        return (d1 && d2)
            ? (d1 > d2 ? accumulator : element)
            : d1 ? accumulator : element;
    }, null);
}

export interface IMinMax {
    minDate: Date | undefined,
    maxDate: Date | undefined
}

export function minMax(dates: MaybeDate[]): IMinMax {
    let initialValue: IMinMax = { minDate: undefined, maxDate: undefined };
    return dates.reduce((accumulator, currentValue) => {
        const date = toDate(currentValue);
        if (date) {
            accumulator.minDate = accumulator.minDate && accumulator.minDate < date
                ? accumulator.minDate
                : date;
            accumulator.maxDate = accumulator.maxDate && accumulator.maxDate > date
                ? accumulator.maxDate
                : date
        }
        return accumulator;
    }, initialValue);
}

export function splitString(str: string | null, externalSeparator?: string) {
    if (!str) {
        return [];
    }
    const separator = externalSeparator || ',';
    const commaSubstitutor = 'comma-substitutor';
    return str
        .replace(new RegExp(separator + separator, 'g'), commaSubstitutor)
        .split(separator)
        .map(_ => _.replace(new RegExp(commaSubstitutor, 'g'), separator));
}

export function calculateFractionDate(startDate: Date, finishDate: Date, progress: number) {
    const duration = diffDays(startDate, finishDate);
    return startDate.clone().addDays(duration * progress);
}

export function diffDays(startDate: Date, finishDate: Date, keepSign?: boolean) {
    const diff = (finishDate.getTime() - startDate.getTime()) / MILLISECONDS_PER_DAY;
    return keepSign ? diff : Math.abs(diff);
}

export function dateRangeOverlaps(a_start: Date, a_end: Date, b_start: Date, b_end: Date) {
    b_start = b_start || b_end;
    b_end = b_end || b_start;
    return a_start <= b_end! && b_start! <= a_end;
}

export function remainingDaysToString(finish: string | Date | undefined, isTrial?: boolean): string {
    const finishDate = toDate(finish);
    if (!finishDate) {
        return "";
    }

    if (finishDate < new Date().getBeginOfDay()) {
        return isTrial ? "" : "Expired";
    }

    const count = remainDays(finish)
    return count == 0 ? "Expires today" : `${count}${isTrial ? " trial" : ""} day${count == 1 ? '' : 's'} left`;
}

export function remainDays(finish: string | Date | undefined): number | undefined {
    const finishDate = toDate(finish);

    if (!finishDate)
        return undefined;

    const now = new Date();
    if (now > finishDate) {
        return 0;
    }

    finishDate.setHours(24, 0, 0, 0);
    return Math.floor(diffDays(finishDate, now));
}

export function printDaysOfWeek(days: DayOfWeek[]): string {
    return days
        .filter((_, idx, arr) => arr.indexOf(_) === idx)
        .sort()
        .map((_, idx, arr) => {
            if (idx === 0) {
                return DayOfWeek[_];
            }
            if ((arr[idx - 1] + 1) !== _) {
                return ', ' + DayOfWeek[_];
            }
            if (arr.length >= idx && _ === (arr[idx + 1] - 1)) {
                return ' - ';
            }
            const prev2_offset = 2;
            return (idx > 1 && arr[idx - prev2_offset] + prev2_offset === _) ? DayOfWeek[_] : (', ' + DayOfWeek[_]);
        }).filter((_, idx, arr) => idx === 0 || arr[idx - 1] !== _).join('');
}

// idField neccesary to make stable sort
export function getComparer(fieldName: string, direction: SortDirection, idField?: string): (a: any, b: any) => number {
    return direction == SortDirection.ASC
        ? (idField
            ? (a, b) => a[fieldName] == b[fieldName] ? (a[idField] > b[idField] ? 1 : -1) : (a[fieldName] > b[fieldName] ? 1 : -1)
            : (a, b) => a[fieldName] == b[fieldName] ? 0 : (a[fieldName] > b[fieldName] ? 1 : -1)
        )
        : (idField
            ? (a, b) => a[fieldName] == b[fieldName] ? (a[idField] > b[idField] ? -1 : 1) : (a[fieldName] > b[fieldName] ? -1 : 1)
            : (a, b) => a[fieldName] == b[fieldName] ? 0 : (a[fieldName] > b[fieldName] ? -1 : 1)
        );
}